import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const POLL_INTERVAL_MS = 3000;

const STATUS_FILTERS = ["all", "active", "completed", "error"];

const ACTIVE_STATUSES = ["pending", "in_progress", "running"];
const COMPLETED_STATUSES = ["completed", "success"]; // keep loose for future
const ERROR_STATUSES = ["failed", "error"];

function formatUtcTimestamp(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss} UTC`;
}

function deriveStepInfo(event, stepsById) {
  const step = event.step_id ? stepsById[event.step_id] : null;
  const rawType = step?.step_type || null;

  let group = "OTHER";
  if (event.event_type === "PLAN_CREATED") {
    group = "PLANNING";
  } else if (rawType === "REASONING") {
    group = "REASONING";
  } else if (rawType === "EMERGENT_ACTION") {
    group = "EMERGENT_ACTION";
  }

  return {
    group,
    stepType: rawType,
    stepStatus: step?.status || null,
    stepLabel: step?.label || null,
  };
}

function computeDurationSeconds(start, end) {
  if (!start || !end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return null;
  return ((e - s) / 1000).toFixed(1);
}

const OrchestratorDashboard = () => {
  const [goals, setGoals] = useState([]);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);

  const [newTitle, setNewTitle] = useState("");
  const [newGoalText, setNewGoalText] = useState("");
  const [creating, setCreating] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [detailTab, setDetailTab] = useState("timeline");
  const [timelineOnlyEmergent, setTimelineOnlyEmergent] = useState(false);

  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(null);

  const [showGuide, setShowGuide] = useState(false);

  const fetchGoals = async (options = { showSpinner: false }) => {
    const { showSpinner } = options;
    try {
      if (showSpinner) {
        setLoadingGoals(true);
      }
      setError(null);
      const res = await fetch("/api/goals");
      if (!res.ok) throw new Error(`Failed to load goals (${res.status})`);
      const data = await res.json();
      setGoals(data || []);
    } catch (err) {
      setError(err.message || "Error loading goals");
    } finally {
      if (showSpinner) {
        setLoadingGoals(false);
      }
    }
  };

  const fetchMetrics = async () => {
    try {
      setMetricsError(null);
      const res = await fetch("/api/internal/orchestrator/health");
      if (!res.ok) throw new Error(`Failed to load metrics (${res.status})`);
      const data = await res.json();
      setMetrics(data);
    } catch (err) {
      setMetricsError(err.message || "Error loading metrics");
    }
  };

  const fetchGoalDetail = async (goalId) => {
    try {
      setLoadingDetail(true);
      setError(null);
      const res = await fetch(`/api/goals/${goalId}`);
      if (!res.ok) throw new Error(`Failed to load goal ${goalId} (${res.status})`);
      const data = await res.json();
      setSelectedGoal(data);
    } catch (err) {
      setError(err.message || "Error loading goal detail");
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    // Initial fetch shows loading state
    fetchGoals({ showSpinner: true });
    fetchMetrics();
    const interval = setInterval(() => {
      // Background refresh avoids flicker by not toggling loading spinner
      fetchGoals({ showSpinner: false });
      fetchMetrics();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!selectedGoalId) return;

    const poll = async () => {
      await fetchGoalDetail(selectedGoalId);
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedGoalId]);

  const createGoal = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newGoalText.trim()) return;
    try {
      setCreating(true);
      setError(null);
      const res = await fetch("/api/goals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: newTitle,
          goal: newGoalText,
          userId: "dashboard",
        }),
      });
      if (!res.ok) throw new Error(`Failed to create goal (${res.status})`);
      const data = await res.json();
      const goalId = data.goalId;
      setNewTitle("");
      setNewGoalText("");
      await fetchGoals();
      setSelectedGoalId(goalId);
      await fetchGoalDetail(goalId);
    } catch (err) {
      setError(err.message || "Error creating goal");
    } finally {
      setCreating(false);
    }
  };

  const filteredGoals = useMemo(() => {
    if (statusFilter === "all") return goals;
    return goals.filter((g) => {
      const s = g.status;
      if (statusFilter === "active") return ACTIVE_STATUSES.includes(s);
      if (statusFilter === "completed") return COMPLETED_STATUSES.includes(s);
      if (statusFilter === "error") return ERROR_STATUSES.includes(s);
      return true;
    });
  }, [goals, statusFilter]);

  const stats = useMemo(() => {
    const total = goals.length;
    const active = goals.filter((g) => ACTIVE_STATUSES.includes(g.status)).length;
    const completed = goals.filter((g) => COMPLETED_STATUSES.includes(g.status)).length;
    const errorCount = goals.filter((g) => ERROR_STATUSES.includes(g.status)).length;
    return { total, active, completed, error: errorCount };
  }, [goals]);

  const groupedEvents = useMemo(() => {
    if (!selectedGoal) return [];
    const stepsById = Object.fromEntries(
      (selectedGoal.steps || []).map((s) => [s.id, s])
    );

    const groups = new Map();

    (selectedGoal.events || []).forEach((event) => {
      const info = deriveStepInfo(event, stepsById);
      const groupKey = info.group;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey).push({ event, info });
    });

    let entries = Array.from(groups.entries()).map(([group, items]) => ({
      group,
      items,
    }));

    if (timelineOnlyEmergent) {
      const emergentTypes = new Set(["EMERGENT_ACTION"]);
      entries = entries
        .map(({ group, items }) => ({
          group,
          items: items.filter(({ event, info }) =>
            ["EMERGENT_CALL", "EMERGENT_ERROR"].includes(event.event_type) || emergentTypes.has(info.stepType)
          ),
        }))
        .filter(({ items }) => items.length > 0);
    }

    return entries;
  }, [selectedGoal, timelineOnlyEmergent]);

  const lastEmergentEvent = useMemo(() => {
    if (!selectedGoal) return null;
    const emergentEvents = (selectedGoal.events || []).filter((e) =>
      ["EMERGENT_CALL", "EMERGENT_ERROR"].includes(e.event_type)
    );
    if (emergentEvents.length === 0) return null;
    return emergentEvents[emergentEvents.length - 1];
  }, [selectedGoal]);

  const renderStatusBadge = (status) => {
    let variantClass = "bg-slate-700 text-slate-100";
    if (status === "completed") variantClass = "bg-emerald-600 text-emerald-50";
    else if (status === "failed") variantClass = "bg-red-600 text-red-50";
    else if (status === "in_progress") variantClass = "bg-amber-500 text-amber-50";

    return (
      <Badge
        data-testid="goal-status-badge"
        className={`uppercase tracking-wide text-[0.65rem] px-2 py-0.5 rounded-full ${variantClass}`}
      >
        {status}
      </Badge>
    );
  };

  const workerStatus = useMemo(() => {
    if (!metrics || !metrics.lastLoopHeartbeat) return "unknown";
    const last = new Date(metrics.lastLoopHeartbeat).getTime();
    const now = Date.now();
    if (Number.isNaN(last)) return "unknown";
    const diffSec = (now - last) / 1000;
    if (diffSec <= 2 * (POLL_INTERVAL_MS / 1000)) return "healthy";
    if (diffSec <= 60) return "stale";
    return "stale";
  }, [metrics]);

  const failureRatePct = useMemo(() => {
    if (!metrics || metrics.metrics.failureRateLast24h == null) return "n/a";
    return `${(metrics.metrics.failureRateLast24h * 100).toFixed(1)}%`;
  }, [metrics]);

  const durationStats = useMemo(() => {
    if (!metrics || !metrics.metrics.durationStatsLast24h) return { p50: "n/a", p90: "n/a" };
    const { p50, p90 } = metrics.metrics.durationStatsLast24h;
    return {
      p50: p50 != null ? `${p50.toFixed?.(1) || p50}s` : "n/a",
      p90: p90 != null ? `${p90.toFixed?.(1) || p90}s` : "n/a",
    };
  }, [metrics]);

  const modeBreakdown = useMemo(() => {
    if (!metrics || !metrics.metrics.modeBreakdownLast24h) {
      return {
        direct: { totalGoals: "-", failedGoals: "-" },
        queue: { totalGoals: "-", failedGoals: "-" },
      };
    }
    return metrics.metrics.modeBreakdownLast24h;
  }, [metrics]);

  return (
    <div className="h-screen flex flex-col bg-slate-950 text-slate-50" data-testid="orchestrator-dashboard">
      <header className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-lg font-semibold leading-tight" data-testid="orchestrator-title">
              SIOX Orchestrator
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              Plan, execute, and observe AI-driven delivery flows in real time.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs text-slate-300 hover:text-emerald-200 hover:bg-slate-900/70"
            onClick={() => setShowGuide(true)}
            data-testid="open-user-guide-button"
          >
            Open user guide
          </Button>
        </div>
        <div className="flex items-stretch gap-3" data-testid="metrics-panel">
          <Card className="bg-slate-900/60 border-slate-800 min-w-[260px] text-slate-100">
            <CardHeader className="p-3 pb-1">
              <CardTitle className="text-xs text-slate-200">Orchestrator Metrics (24h)</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 text-[0.7rem] space-y-1 text-slate-100">
              {metricsError && (
                <div className="text-red-400" data-testid="metrics-error">
                  {metricsError}
                </div>
              )}
              {!metrics && !metricsError && (
                <div className="text-slate-300" data-testid="metrics-loading">Loading metrics…</div>
              )}
              {metrics && (
                <>
                  <div>
                    Total goals: <span className="font-semibold">{metrics.metrics.totalGoals}</span>
                  </div>
                  <div>
                    Goals (24h): <span className="font-semibold">{metrics.metrics.goalsLast24h}</span>
                  </div>
                  <div>
                    Completed (24h):
                    <span className="font-semibold ml-1">
                      {metrics.metrics.statusBreakdown.completed}
                    </span>
                  </div>
                  <div>
                    Failed (24h):
                    <span className="font-semibold ml-1">
                      {metrics.metrics.statusBreakdown.failed}
                    </span>
                  </div>
                  <div>
                    Avg duration (24h):
                    <span className="font-semibold ml-1">
                      {metrics.metrics.avgDurationSecondsLast24h
                        ? `${metrics.metrics.avgDurationSecondsLast24h.toFixed?.(1) || metrics.metrics.avgDurationSecondsLast24h}s`
                        : "n/a"}
                    </span>
                  </div>
                  <div>
                    Failure rate (24h):
                    <span className="font-semibold ml-1">{failureRatePct}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span>Latency P50/P90:</span>
                    <span className="font-semibold ml-1">
                      {durationStats.p50} / {durationStats.p90}
                    </span>
                  </div>
                  <div className="text-[0.65rem] text-slate-400 mt-1">
                    Direct: {modeBreakdown.direct.totalGoals} total, {modeBreakdown.direct.failedGoals} failed
                    <br />
                    Queue: {modeBreakdown.queue.totalGoals} total, {modeBreakdown.queue.failedGoals} failed
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span>Worker:</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[0.65rem] capitalize ${
                        workerStatus === "healthy"
                          ? "bg-emerald-600/60 text-emerald-50"
                          : workerStatus === "stale"
                          ? "bg-amber-500/40 text-amber-50"
                          : "bg-slate-700 text-slate-100"
                      }`}
                      data-testid="worker-status-pill"
                    >
                      {workerStatus}
                    </span>
                  </div>
                  <div className="text-[0.65rem] text-slate-300">
                    Last heartbeat: {metrics.lastLoopHeartbeat
                      ? formatUtcTimestamp(metrics.lastLoopHeartbeat)
                      : "n/a"}
                  </div>
                  <div className="text-[0.65rem] text-slate-300">
                    Worker errors: {metrics.workerDiagnostics?.errorCount ?? "n/a"}
                    {metrics.workerDiagnostics?.lastErrorAt && (
                      <>
                        {" "} (last at {formatUtcTimestamp(metrics.workerDiagnostics.lastErrorAt)})
                      </>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </header>

      <main className="flex-1 grid grid-cols-[1.6fr_1.4fr] gap-4 p-4 overflow-hidden">
        <section className="flex flex-col gap-3 overflow-hidden" data-testid="goals-section">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm text-slate-100" data-testid="goals-heading">
                  Goals
                </CardTitle>
                <span className="text-[0.7rem] text-slate-200">
                  Total {stats.total}  b7 Active {stats.active}  b7 Completed {stats.completed}  b7 Error {stats.error}
                </span>
              </div>
              <Tabs
                value={statusFilter}
                onValueChange={setStatusFilter}
                className="text-[0.7rem]"
                data-testid="status-filter-tabs"
              >
                <TabsList className="bg-slate-900/80 h-8">
                  {STATUS_FILTERS.map((f) => (
                    <TabsTrigger
                      key={f}
                      value={f}
                      className="px-3 h-7 data-[state=active]:bg-slate-700 data-[state=active]:text-slate-50"
                      data-testid={`status-filter-${f}`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="p-0">
              {loadingGoals ? (
                <div className="p-4 space-y-2" data-testid="goals-loading">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Skeleton key={idx} className="h-8 w-full bg-slate-800/80" />
                  ))}
                </div>
              ) : filteredGoals.length === 0 ? (
                <div className="p-6 text-sm text-slate-400" data-testid="goals-empty-state">
                  No goals match this filter. Create a new goal to get started.
                </div>
              ) : (
                <ScrollArea className="max-h-[420px]" data-testid="goals-scroll-area">
                  <div className="divide-y divide-slate-800" data-testid="goals-list">
                    {filteredGoals.map((goal) => (
                      <button
                        key={goal.id}
                        type="button"
                        onClick={() => setSelectedGoalId(goal.id)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-900/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                          goal.id === selectedGoalId ? "bg-slate-900" : ""
                        }`}
                        data-testid="goal-row"
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-slate-400 truncate" data-testid="goal-id">
                              {goal.id}
                            </span>
                            {renderStatusBadge(goal.status)}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-sm font-medium truncate" data-testid="goal-title">
                              {goal.title || "Untitled goal"}
                            </span>
                          </div>
                          <span className="text-[0.7rem] text-slate-500 mt-1 truncate" data-testid="goal-updated-at">
                            Updated {goal.updated_at ? formatUtcTimestamp(goal.updated_at) : "n/a"}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {error && (
                <div
                  className="px-4 py-2 text-xs text-red-400 border-t border-red-500/40 bg-red-950/30"
                  data-testid="goals-error-banner"
                >
                  {error}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-900/60 border-slate-800" data-testid="create-goal-card">
            <CardHeader className="px-4 py-3 border-b border-slate-800">
              <CardTitle className="text-sm text-slate-100">Create Goal</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <form className="space-y-3" onSubmit={createGoal} data-testid="create-goal-form">
                <div className="space-y-1">
                  <label className="text-xs text-slate-400" htmlFor="goal-title">
                    Title
                  </label>
                  <Input
                    id="goal-title"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="E.g. Build a customer billing microservice"
                    className="bg-slate-900 border-slate-700 text-xs text-slate-100 placeholder:text-slate-500"
                    data-testid="create-goal-title-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-400" htmlFor="goal-text">
                    Goal description
                  </label>
                  <Textarea
                    id="goal-text"
                    value={newGoalText}
                    onChange={(e) => setNewGoalText(e.target.value)}
                    placeholder="Describe the high-level outcome you want the orchestrator to achieve."
                    className="bg-slate-900 border-slate-700 text-xs min-h-[80px] resize-y text-slate-100 placeholder:text-slate-500"
                    data-testid="create-goal-text-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-400" htmlFor="goal-file">
                    Optional directives file
                  </label>
                  <Input
                    id="goal-file"
                    type="file"
                    accept=".txt,.md,.pdf,.docx,.xlsx,.xls"
                    className="bg-slate-900 border-slate-700 text-xs file:text-xs file:bg-slate-800 file:border-0 file:mr-2 file:px-3 file:py-1 file:text-slate-200"
                    data-testid="create-goal-file-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const name = file.name || "uploaded-file";
                      const lower = name.toLowerCase();
                      const isTextLike = [".txt", ".md", ".pdf"].some((ext) => lower.endsWith(ext));
                      if (!isTextLike) {
                        // For Office formats, we currently only record a reference, not full text extraction.
                        setNewGoalText((prev) =>
                          prev
                            ? `${prev}\n\n---\nUploaded directives file (not auto-parsed yet): ${name}`
                            : `Uploaded directives file (not auto-parsed yet): ${name}`,
                        );
                        return;
                      }

                      const reader = new FileReader();
                      reader.onload = (evt) => {
                        const text = evt.target?.result;
                        if (typeof text === "string") {
                          setNewGoalText((prev) =>
                            prev ? `${prev}\n\n---\nUploaded directives:\n${text}` : text,
                          );
                        }
                      };
                      reader.readAsText(file);
                    }}
                  />
                  <p className="text-[0.65rem] text-slate-500">
                    You can upload .txt, .md, .pdf, .docx, .xlsx, or .xls files. Text formats are appended into the
                    description automatically; Office formats are referenced by file name so you know which document was used.
                  </p>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-[0.7rem] text-slate-300">
                    Goals will start immediately using the current orchestrator mode.
                  </span>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={creating || !newTitle.trim() || !newGoalText.trim()}
                    className="bg-emerald-600 hover:bg-emerald-500 text-xs px-3"
                    data-testid="create-goal-submit-button"
                  >
                    {creating ? "Creating..." : "Create goal"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </section>

        <section className="flex flex-col gap-3 overflow-hidden" data-testid="detail-section">
          <Card className="bg-slate-900/60 border-slate-800 flex-1 flex flex-col min-h-0">
            <CardHeader className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
              <div className="flex flex-col">
                <CardTitle className="text-sm text-slate-100" data-testid="detail-heading">
                  {selectedGoal ? selectedGoal.goal.title || "Goal detail" : "Select a goal"}
                </CardTitle>
                {selectedGoal && (
                  <span className="text-[0.7rem] text-slate-300" data-testid="detail-goal-id">
                    {selectedGoal.goal.id}
                  </span>
                )}
              </div>
              {selectedGoal && (
                <div className="flex flex-col items-end text-[0.7rem] text-slate-200">
                  <span>
                    Created {selectedGoal.goal.created_at ? formatUtcTimestamp(selectedGoal.goal.created_at) : "n/a"}
                  </span>
                  <span>
                    Updated {selectedGoal.goal.updated_at ? formatUtcTimestamp(selectedGoal.goal.updated_at) : "n/a"}
                  </span>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0 flex-1 flex flex-col min-h-0">
              {!selectedGoal ? (
                <div className="h-full flex items-center justify-center text-xs text-slate-500" data-testid="detail-empty-state">
                  Select a goal from the left to inspect its timeline and trace.
                </div>
              ) : (
                <Tabs
                  value={detailTab}
                  onValueChange={setDetailTab}
                  className="flex flex-col h-full"
                  data-testid="detail-tabs"
                >
                  <div className="px-4 pt-3">
                    <TabsList className="bg-slate-900/80 h-8 flex items-center justify-between pr-1">
                      <div className="flex items-center gap-1">
                        <TabsTrigger
                          value="timeline"
                          className="px-3 h-7 text-xs"
                          data-testid="detail-tab-timeline"
                        >
                          Timeline
                        </TabsTrigger>
                        <TabsTrigger
                          value="trace"
                          className="px-3 h-7 text-xs"
                          data-testid="detail-tab-trace"
                        >
                          Trace
                        </TabsTrigger>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTimelineOnlyEmergent((v) => !v)}
                        className={`text-[0.65rem] px-2 py-1 rounded border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                          timelineOnlyEmergent
                            ? "bg-emerald-600/20 border-emerald-400 text-emerald-200"
                            : "bg-transparent border-slate-700 text-slate-400 hover:bg-slate-900/60"
                        }`}
                        data-testid="timeline-only-emergent-toggle"
                      >
                        {timelineOnlyEmergent ? "Showing Emergent only" : "Show Emergent only"}
                      </button>
                    </TabsList>
                  </div>
                  <div className="flex-1 min-h-0">
                    <TabsContent
                      value="timeline"
                      className="h-full data-[state=inactive]:hidden"
                      data-testid="detail-timeline-content"
                    >
                      <ScrollArea className="h-full px-4 pb-4" data-testid="timeline-scroll-area">
                        {groupedEvents.length === 0 ? (
                          <div className="text-xs text-slate-500 mt-4">No events recorded yet for this goal.</div>
                        ) : (
                          <div className="space-y-4" data-testid="timeline-groups">
                            {groupedEvents.map((group) => (
                              <div key={group.group} data-testid="timeline-group">
                                <div className="text-[0.7rem] font-semibold text-slate-400 mb-2">
                                  {group.group}
                                </div>
                                <div className="space-y-2 border-l border-slate-700 pl-4" data-testid="timeline-rail">
                                  {group.items.map(({ event, info }) => (
                                    <div
                                      key={event.id}
                                      className="relative flex flex-col gap-0.5 text-[0.7rem] text-slate-200"
                                      data-testid="event-item"
                                    >
                                      <div className="absolute -left-2 top-1 w-2 h-2 rounded-full bg-emerald-500" />
                                      <div className="flex items-center justify-between">
                                        <span className="font-mono text-[0.65rem] text-slate-400" data-testid="event-timestamp">
                                          {event.created_at ? formatUtcTimestamp(event.created_at) : "n/a"}
                                        </span>
                                        <span className="text-[0.65rem] text-slate-500">
                                          {info.stepLabel || event.event_type}
                                        </span>
                                      </div>
                                      <div className="text-[0.7rem] text-slate-300">
                                        {event.summary || event.event_type}
                                      </div>
                                      {event.correlation_id && (
                                        <div className="flex items-center gap-2 text-[0.65rem] text-slate-500 mt-0.5" data-testid="event-correlation-id">
                                          <span>Correlation ID:</span>
                                          <span className="font-mono truncate max-w-[220px]">
                                            {event.correlation_id}
                                          </span>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 px-2 text-[0.65rem] text-slate-300 hover:text-emerald-200 hover:bg-slate-800/80"
                                            onClick={() => navigator.clipboard?.writeText(event.correlation_id)}
                                            data-testid="event-correlation-copy-button"
                                          >
                                            Copy
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </TabsContent>

                    <TabsContent
                      value="trace"
                      className="h-full data-[state=inactive]:hidden"
                      data-testid="detail-trace-content"
                    >
                      <ScrollArea className="h-full px-4 pb-4" data-testid="trace-scroll-area">
                        <div className="space-y-3 mt-3">
                          <div className="text-xs text-slate-400">Steps</div>
                          <div className="space-y-2" data-testid="trace-steps">
                            {(selectedGoal.steps || []).map((step) => {
                              const duration = computeDurationSeconds(step.started_at, step.completed_at);
                              return (
                                <div
                                  key={step.id}
                                  className="flex items-center gap-3 text-[0.7rem]"
                                  data-testid="trace-step-row"
                                >
                                  <span className="text-[0.65rem] text-slate-500 w-6">#{step.order_index}</span>
                                  <span className="w-28 text-[0.65rem] uppercase tracking-wide text-slate-400">
                                    {step.step_type}
                                  </span>
                                  <span className="flex-1 truncate">{step.label}</span>
                                  <span className="w-20 text-right text-[0.65rem] text-slate-400">
                                    {duration ? `${duration}s` : "-"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          <div className="mt-4">
                            <div className="text-xs text-slate-400 mb-1">Last Emergent call</div>
                            {!lastEmergentEvent ? (
                              <div className="text-[0.7rem] text-slate-500" data-testid="trace-no-emergent">
                                No Emergent calls recorded yet.
                              </div>
                            ) : (
                              <pre
                                className="bg-slate-900 border border-slate-800 rounded p-2 text-[0.65rem] font-mono overflow-auto max-h-48"
                                data-testid="trace-emergent-json"
                              >
                                {JSON.stringify(lastEmergentEvent, null, 2)}
                              </pre>
                            )}
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </div>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </section>
      </main>
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-slate-100" data-testid="user-guide-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">How to use the SIOX Orchestrator dashboard</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Follow these steps to create goals, monitor orchestration, and debug with traces and correlation IDs.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[420px] pr-4" data-testid="user-guide-scroll">
            <div className="space-y-4 text-xs leading-relaxed">
              <section>
                <h2 className="text-sm font-semibold text-slate-100 mb-1">1. Create a new goal</h2>
                <ol className="list-decimal list-inside space-y-1 text-slate-300">
                  <li>In the <span className="font-semibold">Create Goal</span> card on the left, enter a concise title.</li>
                  <li>Describe the high-level outcome you want (what you want built, not how).</li>
                  <li>
                    Optionally upload a <span className="font-mono">.txt</span>, <span className="font-mono">.md</span>, or
                    <span className="font-mono"> .pdf</span> file with full directives. Its text will be appended into the
                    goal description so GPT can follow your detailed instructions.
                  </li>
                  <li>Click <span className="font-semibold">Create goal</span>. In direct mode, orchestration starts immediately.</li>
                </ol>
              </section>

              <section>
                <h2 className="text-sm font-semibold text-slate-100 mb-1">2. Track goals in the list</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>The <span className="font-semibold">Goals</span> panel shows all goals with ID, title, status, and last update time.</li>
                  <li>Use the status tabs (<span className="font-mono">All / Active / Completed / Error</span>) to filter.</li>
                  <li>Click any row to open its detailed timeline and trace on the right.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-sm font-semibold text-slate-100 mb-1">3. Understand the Timeline</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>The <span className="font-semibold">Timeline</span> groups events into <span className="font-mono">PLANNING</span>, <span className="font-mono">REASONING</span>, <span className="font-mono">EMERGENT_ACTION</span>, and <span className="font-mono">OTHER</span>.</li>
                  <li>Each event shows a UTC timestamp, label, and summary of what happened.</li>
                  <li>For Emergent calls, you will see a <span className="font-semibold">Correlation ID</span> line with a one-click <span className="font-mono">Copy</span> button.</li>
                  <li>Use the <span className="font-mono">Show Emergent only</span> toggle to focus the timeline on builder-related activity.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-sm font-semibold text-slate-100 mb-1">4. Use the Trace tab for debugging</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>The <span className="font-semibold">Trace</span> tab lists all steps with order, type, label, and duration.</li>
                  <li>The bottom section shows the last Emergent call as JSON, including request, response, and <span className="font-mono">correlation_id</span>.</li>
                  <li>Use correlation IDs to cross-reference logs, Builder traces, and Mongo events.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-sm font-semibold text-slate-100 mb-1">5. Read orchestrator health & metrics</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>The header metrics card pulls from <span className="font-mono">/api/internal/orchestrator/health</span>.</li>
                  <li>It shows throughput, 24h success/failure, latency P50/P90, and direct vs queue breakdown.</li>
                  <li>The worker pill indicates whether the background worker looks <span className="font-mono">healthy</span> or <span className="font-mono">stale</span>.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-sm font-semibold text-slate-100 mb-1">6. Direct vs queue mode</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>In <span className="font-semibold">direct mode</span>, goals orchestrate during the HTTP request. The UI will show the goal as completed within a few seconds for simple flows.</li>
                  <li>In <span className="font-semibold">queue mode</span>, goals start as <span className="font-mono">pending</span> and are picked up by a worker; watch the status and metrics panel to see progress.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-sm font-semibold text-slate-100 mb-1">7. Recommended usage pattern</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>Start with a clear, concise goal and optional directives file.</li>
                  <li>Wait for orchestration to finish, then inspect the Timeline for high-level flow.</li>
                  <li>Use the Trace + Correlation IDs if something looks off or fails.</li>
                  <li>Iterate on your goal description/directives to refine behavior.</li>
                </ul>
              </section>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrchestratorDashboard;
